import React, { useState } from 'react';
import {
  Activity, Heart, Shield, HeartPulse, Droplet, Pill, Wind, Bone, Brain, Syringe,
  ArrowRight, Upload, MessageCircle, Phone, CheckCircle2, ShieldCheck,
  Stethoscope, Thermometer, Microscope, TestTube, Eye, Ear
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSiteData } from '../context/SiteDataContext';

const MinimalPharmacyPage = () => {
  const navigate = useNavigate();
  const { siteData } = useSiteData();
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    phone: '',
    condition: '',
    message: ''
  });
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const iconMap = {
    Activity, Heart, Shield, HeartPulse, Droplet, Pill, Wind, Bone, Brain, Syringe,
    Stethoscope, Thermometer, Microscope, TestTube, Eye, Ear
  };

  const handleWhatsApp = () => {
    // Extract numbers only to ensure formatting works with API
    const cleanNumber = siteData.general.whatsappNumber.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanNumber}`, '_blank');
  };

  const handleLink = (url) => {
    if (!url) return;
    if (url.startsWith('#')) {
      const element = document.querySelector(url);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (url.startsWith('http')) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  const handlePrescriptionUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPrescriptionFile(file);
      toast.success(`Prescription uploaded successfully!`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const sheetUrl = siteData.general.googleSheetUrl;
    if (sheetUrl) {
      try {
        const payload = new FormData();
        payload.append('Name', consultationForm.name);
        payload.append('Email', consultationForm.email);
        payload.append('Phone', consultationForm.phone);
        payload.append('Condition', consultationForm.condition);
        payload.append('Message', consultationForm.message);
        payload.append('Timestamp', new Date().toLocaleString());

        // Note: Free Google Apps Script Web Apps require no-cors for simple POSTs
        await fetch(sheetUrl, {
          method: 'POST',
          mode: 'no-cors',
          body: payload
        });
      } catch (error) {
        console.error('Submission error:', error);
      }
    }

    setIsSubmitting(false);
    toast.success('Thank you! Our team will contact you within 24 hours.');
    setConsultationForm({ name: '', email: '', phone: '', condition: '', message: '' });
    setPrescriptionFile(null);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Simple Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2 shrink-0">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg md:text-xl">{siteData.general.siteName.charAt(0)}</span>
              </div>
              <span className="text-lg md:text-xl font-bold text-gray-900 truncate">{siteData.general.siteName}</span>
            </div>
            <div className="flex items-center space-x-2 md:space-x-3">
              <Button onClick={handleWhatsApp} size="sm" className="bg-green-600 hover:bg-green-700 text-white px-3 md:px-4">
                <MessageCircle className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">WhatsApp</span>
              </Button>
              <Button onClick={() => handleLink(siteData.general.headerCtaLink)} size="sm" className="bg-red-600 hover:bg-red-700 text-white px-3 md:px-4">
                <span className="text-xs md:text-sm">{siteData.general.headerCtaText}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero - Simple & Clear */}
      <section className="pt-28 pb-16 md:pt-32 md:pb-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              {siteData.minimalHero.titlePart1} <br className="hidden sm:block" />
              <span className="text-red-600">{siteData.minimalHero.titleHighlight}</span>
              {siteData.hero.titlePart2 && <><br className="hidden sm:block" />{siteData.hero.titlePart2}</>}
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 leading-relaxed whitespace-pre-line max-w-3xl mx-auto">
              {siteData.minimalHero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6 px-4">
              <Button onClick={() => handleLink(siteData.minimalHero.cta1Link)} size="lg" className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white px-8 md:px-10 py-6 text-lg rounded-lg shadow-lg">
                {siteData.minimalHero.cta1Text}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={() => handleLink(siteData.minimalHero.cta2Link)} size="lg" variant="outline" className="w-full sm:w-auto border-2 border-gray-800 text-gray-800 hover:bg-gray-50 px-8 md:px-10 py-6 text-lg rounded-lg">
                <Upload className="w-5 h-5 mr-2" />
                {siteData.minimalHero.cta2Text}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Disease Categories - Main Focus */}
      <section id="diseases" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 px-2">
                {siteData.sections.categoriesHeader}
              </h2>
              <p className="text-lg md:text-xl text-gray-600 px-4">
                {siteData.sections.categoriesSub}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {siteData.categories.map((category) => {
                const Icon = iconMap[category.icon] || Activity;
                return (
                  <button
                    key={category.id}
                    onClick={() => navigate(`/category/${category.id}`)}
                    className="group bg-white rounded-xl p-4 md:p-6 border-2 border-gray-200 hover:border-red-600 hover:shadow-xl transition-all duration-300 text-center flex flex-col items-center"
                  >
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-red-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                      <Icon className="w-6 h-6 md:w-8 md:h-8 text-red-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">{category.productCount}+ medications</p>
                    <p className="text-xs text-green-600 font-bold">Save up to {category.avgSavings}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Cost Savings - Simple & Powerful */}
      <section id="savings" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {siteData.sections.savingsHeader}
              </h2>
              <p className="text-xl text-gray-600">
                {siteData.sections.savingsSub}
              </p>
            </div>

            <div className="space-y-4">
              {siteData.savings.slice(0, 4).map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-4 md:p-6 shadow-md md:shadow-lg border border-gray-200 overflow-hidden">
                  <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 items-center">
                    <div className="col-span-1 sm:col-span-2 md:col-span-1">
                      <p className="text-xs md:text-sm text-gray-600 mb-1">{item.category}</p>
                      <p className="font-bold text-gray-900 text-base md:text-lg">{item.medication}</p>
                    </div>
                    <div className="text-center p-3 bg-gray-50 md:bg-transparent rounded-lg md:rounded-none">
                      <p className="text-xs md:text-sm text-gray-600 mb-1 uppercase tracking-wider md:normal-case md:tracking-normal">US Price</p>
                      <p className="text-xl md:text-2xl font-bold text-red-600">{item.usPrice}</p>
                      <p className="text-[10px] md:text-xs text-gray-500">per month</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 md:bg-transparent rounded-lg md:rounded-none">
                      <p className="text-xs md:text-sm text-gray-600 mb-1 uppercase tracking-wider md:normal-case md:tracking-normal">Our Price</p>
                      <p className="text-xl md:text-2xl font-bold text-green-600">{item.indianPrice}</p>
                      <p className="text-[10px] md:text-xs text-gray-500">per month</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 md:bg-transparent rounded-lg md:rounded-none col-span-1 sm:col-span-2 md:col-span-1">
                      <p className="text-xs md:text-sm text-gray-600 mb-1 uppercase tracking-wider md:normal-case md:tracking-normal">You Save</p>
                      <p className="text-2xl md:text-3xl font-bold text-green-600">{item.savings}</p>
                      <p className="text-xs md:text-sm text-gray-600 font-medium">{item.savingsPercent} less</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600">
                These are real prices. Verified savings for patients worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Promise - Brief & Impactful */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {siteData.sections.qualityPromiseHeader}
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                {siteData.sections.qualityPromiseSub}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {siteData.qualityPromise.map((promise, index) => {
                const Icon = iconMap[promise.icon] || ShieldCheck;
                return (
                  <div key={index} className="text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-10 h-10 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{promise.title}</h3>
                    <p className="text-gray-600">
                      {promise.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* How to Order - Simple Steps */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {siteData.sections.howItWorksHeader}
              </h2>
              <p className="text-xl text-gray-600">
                {siteData.sections.howItWorksSub}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {siteData.howToOrderMinimal.map((step, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg text-center">
                  <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Simple Testimonial */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-6 md:p-12 shadow-xl border-2 border-red-100 text-center relative overflow-hidden">
              <div className="absolute top-4 left-4 text-red-100 opacity-50">
                <Shield className="w-12 h-12 md:w-20 md:h-20" />
              </div>
              <p className="text-xl md:text-2xl text-gray-700 italic mb-8 relative z-10 leading-relaxed">
                {siteData.pinnedTestimonial.quote}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 relative z-10">
                <img src={siteData.pinnedTestimonial.image} alt="Patient" className="w-16 h-16 rounded-full border-4 border-white shadow-lg" />
                <div className="sm:text-left text-center">
                  <p className="font-bold text-gray-900">{siteData.pinnedTestimonial.author}</p>
                  <p className="text-sm text-gray-600">{siteData.pinnedTestimonial.condition}</p>
                  <p className="text-sm text-green-600 font-bold">{siteData.pinnedTestimonial.statistic}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form - Clean */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {siteData.sections.consultationHeader}
              </h2>
              <p className="text-xl text-gray-600">
                {siteData.sections.consultationSub}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 shadow-xl space-y-6">
              <div>
                <Label htmlFor="name">{siteData.contactForm.nameLabel}</Label>
                <Input
                  id="name"
                  value={consultationForm.name}
                  onChange={(e) => setConsultationForm({ ...consultationForm, name: e.target.value })}
                  required
                  className="mt-2 h-12"
                  placeholder={siteData.contactForm.namePlaceholder}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email">{siteData.contactForm.emailLabel}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={consultationForm.email}
                    onChange={(e) => setConsultationForm({ ...consultationForm, email: e.target.value })}
                    required
                    className="mt-2 h-12"
                    placeholder={siteData.contactForm.emailPlaceholder}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">{siteData.contactForm.phoneLabel}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={consultationForm.phone}
                    onChange={(e) => setConsultationForm({ ...consultationForm, phone: e.target.value })}
                    required
                    className="mt-2 h-12"
                    placeholder={siteData.contactForm.phonePlaceholder}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="condition">{siteData.contactForm.conditionLabel}</Label>
                <Input
                  id="condition"
                  value={consultationForm.condition}
                  onChange={(e) => setConsultationForm({ ...consultationForm, condition: e.target.value })}
                  required
                  className="mt-2 h-12"
                  placeholder={siteData.contactForm.conditionPlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="prescription">{siteData.contactForm.prescriptionLabel}</Label>
                <div className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-red-400 transition-colors cursor-pointer">
                  <input
                    type="file"
                    id="prescription"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handlePrescriptionUpload}
                    className="hidden"
                  />
                  <label htmlFor="prescription" className="cursor-pointer flex flex-col items-center">
                    <Upload className="w-12 h-12 text-red-600 mb-3" />
                    <span className="font-semibold text-gray-900">{siteData.contactForm.prescriptionSub}</span>
                    <span className="text-sm text-gray-600 mt-1">{siteData.contactForm.prescriptionTypes}</span>
                    {prescriptionFile && (
                      <span className="mt-3 text-sm text-green-600 font-semibold">
                        ✓ {prescriptionFile.name}
                      </span>
                    )}
                  </label>
                </div>
              </div>

              <div>
                <Label htmlFor="message">{siteData.contactForm.messageLabel}</Label>
                <Textarea
                  id="message"
                  value={consultationForm.message}
                  onChange={(e) => setConsultationForm({ ...consultationForm, message: e.target.value })}
                  className="mt-2"
                  rows={4}
                  placeholder={siteData.contactForm.messagePlaceholder}
                />
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full bg-red-600 hover:bg-red-700 text-white py-4 text-lg font-bold rounded-lg shadow-lg">
                {isSubmitting ? "Sending..." : siteData.contactForm.submitBtnText}
              </Button>

              <p className="text-sm text-gray-600 text-center">
                {siteData.contactForm.responseDisclamer}
              </p>
            </form>

            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">{siteData.contactForm.chatPrompt}</p>
              <Button onClick={handleWhatsApp} size="lg" className="bg-green-600 hover:bg-green-700 text-white">
                <MessageCircle className="w-5 h-5 mr-2" />
                {siteData.contactForm.chatBtnText}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-gray-900 text-white py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg md:text-xl">{siteData.general.siteName.charAt(0)}</span>
              </div>
              <span className="text-xl md:text-2xl font-bold">{siteData.general.siteName}</span>
            </div>
            <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              {siteData.footer.description}
            </p>
            <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-xs md:text-sm uppercase tracking-wider">
              <button onClick={() => handleLink(siteData.footer.link1Url)} className="text-gray-400 hover:text-white transition-colors">{siteData.footer.link1Text}</button>
              <button onClick={() => handleLink(siteData.footer.link2Url)} className="text-gray-400 hover:text-white transition-colors">{siteData.footer.link2Text}</button>
              <button onClick={() => handleLink(siteData.footer.link3Url)} className="text-gray-400 hover:text-white transition-colors">{siteData.footer.link3Text}</button>
            </div>
            <div className="pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-[10px] md:text-xs">© {new Date().getFullYear()} {siteData.general.siteName}. All rights reserved. | Licensed International Pharmacy</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-4 right-4 md:bottom-8 md:right-8 bg-green-600 hover:bg-green-700 text-white p-4 md:p-5 rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all z-50 flex items-center justify-center"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-6 h-6 md:w-7 md:h-7" />
      </button>
    </div>
  );
};

export default MinimalPharmacyPage;